import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const normalizeUkPhone = (v: string) => {
  const cleaned = v.replace(/[\s\-()]/g, '');
  if (/^\+44\d{10}$/.test(cleaned)) return '0' + cleaned.slice(3);
  if (/^44\d{10}$/.test(cleaned)) return '0' + cleaned.slice(2);
  return cleaned;
};

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  email: z.string().trim().max(255).email('Invalid email').optional().or(z.literal('')),
  phone: z.string().trim().max(20).optional().or(z.literal(''))
    .refine((v) => !v || /^0\d{10}$/.test(normalizeUkPhone(v)), {
      message: 'Enter a UK mobile number (e.g. 07123 456789 or +44 7123 456789)',
    })
    .transform((v) => (v ? normalizeUkPhone(v) : v)),
  address: z.string().trim().max(255).optional().or(z.literal('')),
  city: z.string().trim().max(100).optional().or(z.literal('')),
  postcode: z.string().trim().max(20).optional().or(z.literal('')),
}).refine((d) => !!(d.email && d.email.length) || !!(d.phone && d.phone.length), {
  message: 'At least one of email or phone is required',
  path: ['email'],
});

export type EditCustomerFormValues = z.infer<typeof schema>;

interface EditCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: {
    id: string;
    name: string | null;
    email: string | null;
    phone: string | null;
    address: string | null;
    city?: string | null;
    postcode: string | null;
  } | null;
  onSave: (values: EditCustomerFormValues) => Promise<void> | void;
  saving?: boolean;
}

const EditCustomerDialog: React.FC<EditCustomerDialogProps> = ({ open, onOpenChange, customer, onSave, saving }) => {
  const form = useForm<EditCustomerFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      postcode: '',
    },
  });

  useEffect(() => {
    if (customer && open) {
      form.reset({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        city: (customer as any).city || '',
        postcode: customer.postcode || '',
      });
    }
  }, [customer, open]);

  const onSubmit = async (values: EditCustomerFormValues) => {
    await onSave(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Customer</DialogTitle>
          <DialogDescription>
            Update customer details. Add an email if they need a PDF receipt.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <Label htmlFor="edit-name">Name *</Label>
            <Input id="edit-name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-sm text-destructive mt-1">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="edit-email">Email</Label>
            <Input id="edit-email" type="email" {...form.register('email')} placeholder="customer@example.com" />
            {form.formState.errors.email && (
              <p className="text-sm text-destructive mt-1">{form.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="edit-phone">Phone (UK)</Label>
            <Input id="edit-phone" type="tel" {...form.register('phone')} placeholder="07123 456789" />
            {form.formState.errors.phone && (
              <p className="text-sm text-destructive mt-1">{form.formState.errors.phone.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="edit-address">Address</Label>
            <Input id="edit-address" {...form.register('address')} placeholder="123 High Street" />
          </div>

          <div>
            <Label htmlFor="edit-city">City</Label>
            <Input id="edit-city" {...form.register('city')} placeholder="London" />
          </div>

          <div>
            <Label htmlFor="edit-postcode">Postcode</Label>
            <Input id="edit-postcode" {...form.register('postcode')} placeholder="SW1A 1AA" />
          </div>

          <DialogFooter className="pt-2 gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !form.formState.isDirty}>
              {saving ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCustomerDialog;
